import { ThemeBus } from '@theme/events-bus';
import { EVENTS } from '@theme/event-constants';
import { updateCartBubble } from '@theme/utilities';

const MODAL_ID = 'product-card-quick-add';

const DEFAULT_I18N = {
  addToCart: 'Add to bag',
  addShort: 'Add',
  addedShort: 'Added',
  soldOut: 'Sold out',
  unavailable: 'Unavailable',
  maxInCart: 'Maximum in cart',
  adding: 'Adding…',
  added: 'Added!',
  quantity: 'Quantity',
  chooseVariant: 'Choose options',
};

/**
 * @returns {Record<string, string>}
 */
function getI18n() {
  const el = document.getElementById('product-card-atc-i18n');
  if (!el?.textContent) return { ...DEFAULT_I18N };
  try {
    return { ...DEFAULT_I18N, ...JSON.parse(el.textContent) };
  } catch {
    return { ...DEFAULT_I18N };
  }
}

function getModalSubmitClass() {
  const el = document.getElementById('product-card-atc-config');
  return el?.dataset?.modalSubmitClass?.trim() || 'w-full py-3 px-4 rounded-md text-sm font-medium';
}

/**
 * @param {number} cents
 */
function formatMoney(cents) {
  const currency = window.shopCurrency || 'GBP';
  const locale = window.shopLocale || 'en-GB';
  return new Intl.NumberFormat(locale, { style: 'currency', currency }).format(cents / 100);
}

async function ensureCart() {
  if (window.__cart?.items) return;
  try {
    const res = await fetch('/cart.js');
    if (res.ok) window.__cart = await res.json();
  } catch {
    /* ignore */
  }
}

/**
 * @param {number} variantId
 */
function getQtyInCart(variantId) {
  if (!window.__cart?.items) return 0;
  const item = window.__cart.items.find((i) => i.variant_id == variantId);
  return item ? item.quantity : 0;
}

function shouldApplyB2bRules(variant, product) {
  if (product?.applyB2bQuantityRules !== true) return false;
  const min = Number(variant?.quantity_rule?.min) || 1;
  const step = Number(variant?.quantity_rule?.increment) || 1;
  const max = variant?.quantity_rule?.max;
  return min > 1 || step > 1 || max != null;
}

/**
 * @param {object} variant
 * @param {object} [product]
 * @returns {{min: number, step: number, max: number|null}}
 */
function getQuantityRules(variant, product) {
  const b2bRules = shouldApplyB2bRules(variant, product);

  if (!b2bRules) {
    return { min: 1, step: 1, max: null };
  }

  const min = Math.max(1, parseInt(variant?.quantity_rule?.min, 10) || 1);
  const step = Math.max(1, parseInt(variant?.quantity_rule?.increment, 10) || 1);
  const rawRuleMax = variant?.quantity_rule?.max;

  let max = rawRuleMax == null ? null : Number(rawRuleMax);

  // If inventory is tracked and denied, cap with inventory quantity.
  if (variant?.inventory_management === 'shopify' && variant?.inventory_policy === 'deny') {
    const inventoryMax = Number(variant.inventory_quantity ?? 0);
    if (Number.isFinite(inventoryMax) && inventoryMax >= 1) {
      max = max == null ? inventoryMax : Math.min(max, inventoryMax);
    }
  }

  return { min, step, max };
}

/**
 * @param {number} quantity
 * @param {{min: number, step: number, max: number|null}} rules
 * @returns {number}
 */
function normalizeQuantity(quantity, rules) {
  let normalized = Number.isFinite(quantity) ? quantity : rules.min;

  if (normalized < rules.min) normalized = rules.min;
  if (rules.max != null && normalized > rules.max) normalized = rules.max;

  const remainder = ((normalized - rules.min) % rules.step + rules.step) % rules.step;
  if (remainder !== 0) normalized -= remainder;

  if (normalized < rules.min) normalized = rules.min;
  if (rules.max != null && normalized > rules.max) normalized = rules.max;

  return normalized;
}

/**
 * @param {object} variant
 * @param {number} qty
 */
function canAddVariant(variant, qty, product) {
  if (!variant?.available) return false;
  if (!shouldApplyB2bRules(variant, product)) return true;
  const rules = getQuantityRules(variant, product);
  if (rules.max != null && qty > rules.max) return false;
  if (qty < rules.min || ((qty - rules.min) % rules.step !== 0)) return false;

  if (variant.inventory_management !== 'shopify' || variant.inventory_policy !== 'deny') return true;
  const inv = variant.inventory_quantity ?? 0;
  const inCart = getQtyInCart(variant.id);
  return inv - inCart >= qty;
}

/**
 * @param {number} variantId
 * @param {number} quantity
 */
async function postAddToCart(variantId, quantity) {
  const formData = new FormData();
  formData.append('id', String(variantId));
  formData.append('quantity', String(quantity));
  const response = await fetch('/cart/add.js', {
    method: 'POST',
    headers: { Accept: 'application/json' },
    body: formData,
  });
  const result = await response.json();
  if (result.status && result.status !== 200) {
    return { ok: false, message: result.message || 'Error' };
  }
  if (window.__cart && typeof window.__cart.item_count === 'number') {
    window.__cart.item_count += quantity;
  }
  updateCartBubble();

  const res = await fetch('/cart.js');
  if (res.ok) {
    window.__cart = await res.json();
    updateCartBubble();
  }
  ThemeBus.dispatchEvent(new Event(EVENTS.cartUpdated));
  ThemeBus.dispatchEvent(new Event(EVENTS.cartItemAdded));
  return { ok: true };
}

/**
 * @param {HTMLElement} card
 */
function parseProductJson(card) {
  const script = card.querySelector('script[type="application/json"]');
  if (!script?.textContent) return null;
  try {
    return JSON.parse(script.textContent);
  } catch {
    return null;
  }
}

function escapeHtml(str) {
  const d = document.createElement('div');
  d.textContent = str;
  return d.innerHTML;
}

/**
 * @param {object} productData
 * @param {Record<string, string>} i18n
 */
function openModalWithProduct(productData, i18n) {
  const modal = document.querySelector(`modal-component[data-modal-id="${MODAL_ID}"]`);
  if (!modal) return;

  const body = modal.querySelector('[data-modal-body]');
  if (!body) return;

  const variants = productData.variants || [];
  const firstAvail = variants.find((v) => v.available) || variants[0];

  const optionsHtml = variants
    .map((v) => {
      const sel = v.id === firstAvail?.id ? ' selected' : '';
      const dis = v.available ? '' : ' disabled';
      return `<option value="${v.id}"${sel}${dis}>${escapeHtml(v.title)}</option>`;
    })
    .join('');

  body.innerHTML = `
    <div class="space-y-4" data-pca-modal-root>
      <h3 class="text-base font-semibold pr-8">${escapeHtml(productData.title)}</h3>
      <p class="text-sm" data-pca-modal-price>${formatMoney(firstAvail?.price || 0)}</p>
      <div>
        <label class="block text-xs font-medium mb-1" for="pca-variant-select">${escapeHtml(i18n.chooseVariant)}</label>
        <select
          id="pca-variant-select"
          class="w-full rounded-md border border-input-border text-sm py-2 px-3 bg-white"
          data-pca-variant-select
        >${optionsHtml}</select>
      </div>
      <div class="flex items-center gap-3">
        <label class="text-xs shrink-0" for="pca-qty">${escapeHtml(i18n.quantity)}</label>
        <input
          type="number"
          id="pca-qty"
          min="1"
          step="1"
          value="1"
          class="w-20 rounded-md border border-input-border px-2 py-1.5 text-sm"
          data-pca-qty
        />
      </div>
      <p class="text-sm text-red-600 hidden" data-pca-modal-error role="alert"></p>
      <button type="button" data-pca-modal-submit>
        ${escapeHtml(i18n.addToCart)}
      </button>
    </div>
  `;

  const sel = body.querySelector('[data-pca-variant-select]');
  const priceEl = body.querySelector('[data-pca-modal-price]');
  const submitBtn = body.querySelector('[data-pca-modal-submit]');
  const qtyEl = body.querySelector('[data-pca-qty]');
  const errEl = body.querySelector('[data-pca-modal-error]');

  if (submitBtn) submitBtn.className = getModalSubmitClass();

  const selectedVariant = () => variants.find((x) => x.id === parseInt(sel.value, 10));

  const updatePrice = () => {
    const v = selectedVariant();
    if (v) {
      priceEl.textContent = formatMoney(v.price);

      const rules = getQuantityRules(v, productData);
      const normalized = normalizeQuantity(parseInt(qtyEl.value, 10), rules);
      qtyEl.min = String(rules.min);
      qtyEl.step = String(rules.step);
      if (rules.max != null) {
        qtyEl.max = String(rules.max);
      } else {
        qtyEl.removeAttribute('max');
      }
      qtyEl.value = String(normalized);
    }

    submitBtn.disabled = !v?.available;
  };

  sel.addEventListener('change', updatePrice);
  updatePrice();

  submitBtn.addEventListener('click', async () => {
    errEl.classList.add('hidden');
    errEl.textContent = '';
    const variant = selectedVariant();
    const rules = getQuantityRules(variant, productData);
    const qty = normalizeQuantity(parseInt(qtyEl.value, 10), rules);
    qtyEl.value = String(qty);
    if (!variant) return;

    await ensureCart();
    if (!canAddVariant(variant, qty, productData)) {
      errEl.textContent = i18n.maxInCart;
      errEl.classList.remove('hidden');
      return;
    }

    const prevLabel = submitBtn.textContent;
    submitBtn.setAttribute('aria-busy', 'true');
    submitBtn.disabled = true;
    submitBtn.textContent = i18n.adding;

    let result;
    try {
      result = await postAddToCart(variant.id, qty);
    } finally {
      submitBtn.removeAttribute('aria-busy');
      submitBtn.disabled = false;
      submitBtn.textContent = prevLabel;
    }

    if (result?.ok) {
      ThemeBus.dispatchEvent(new CustomEvent(EVENTS.modalClose, { detail: { id: MODAL_ID } }));
    } else if (result?.message) {
      errEl.textContent = result.message;
      errEl.classList.remove('hidden');
    }
  });

  ThemeBus.dispatchEvent(new CustomEvent(EVENTS.modalOpen, { detail: { id: MODAL_ID } }));
  queueMicrotask(() => {
    sel?.focus();
  });
}

/**
 * @param {HTMLElement} card
 * @param {string} message
 */
function announce(card, message) {
  const live = card.querySelector('[data-product-card-atc-live]');
  if (!live) return;
  live.textContent = message;
  setTimeout(() => {
    live.textContent = '';
  }, 3500);
}

function initProductCardQuickAdd() {
  document.addEventListener('click', async (e) => {
    const trigger = e.target.closest('[data-product-card-atc-trigger]');
    if (!trigger) return;

    e.preventDefault();
    e.stopPropagation();

    if (trigger.disabled) return;
    if (trigger.classList.contains('product-card-atc__trigger--busy')) return;

    const card = trigger.closest('[data-product-card]');
    if (!card) return;

    const i18n = getI18n();
    const productData = parseProductJson(card);
    if (!productData?.variants?.length) return;

    await ensureCart();

    const variants = productData.variants;

    if (variants.length === 1) {
      const v = variants[0];
      if (!v.available) return;
      const rules = getQuantityRules(v, productData);
      const qtyToAdd = rules.min;
      if (!canAddVariant(v, qtyToAdd, productData)) {
        announce(card, i18n.maxInCart);
        return;
      }

      const labelEl = trigger.querySelector('.product-card-atc__text');
      const addedLabel = i18n.addedShort || i18n.added;
      const defaultLabel =
        trigger.dataset.atcDefaultLabel ||
        labelEl?.textContent?.trim() ||
        i18n.addShort;
      if (!trigger.dataset.atcDefaultLabel) {
        trigger.dataset.atcDefaultLabel = defaultLabel;
      }
      if (!trigger.dataset.atcDefaultAriaLabel) {
        trigger.dataset.atcDefaultAriaLabel = trigger.getAttribute('aria-label') || defaultLabel;
      }

      trigger.setAttribute('aria-busy', 'true');
      const result = await postAddToCart(v.id, qtyToAdd);
      trigger.removeAttribute('aria-busy');

      if (result.ok) {
        if (labelEl) labelEl.textContent = addedLabel;
        trigger.setAttribute('aria-label', addedLabel);
        trigger.classList.add('product-card-atc__trigger--busy');
        window.setTimeout(() => {
          if (labelEl) labelEl.textContent = defaultLabel;
          trigger.setAttribute('aria-label', trigger.dataset.atcDefaultAriaLabel || defaultLabel);
          trigger.classList.remove('product-card-atc__trigger--busy');
        }, 2000);
      } else {
        announce(card, result.message || i18n.unavailable);
      }
      return;
    }

    openModalWithProduct(productData, i18n);
  });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initProductCardQuickAdd);
} else {
  initProductCardQuickAdd();
}
